import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, MapPin, Calendar, Clock } from "lucide-react";
import { toast } from "sonner";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/shared/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useI18n } from "@/context/I18nContext";
import { api, unwrap } from "@/lib/api";
import { formatDate } from "@/lib/format";

export function AutoBookingShell({ children }) {
  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
      <Header />
      <div className="flex-1 grid place-items-center px-4 py-16">{children}</div>
      <Footer />
    </div>
  );
}

export function BookingDetails({ booking, t, locale }) {
  return (
    <div className="rounded-xl bg-[#F5F5F4] p-4 space-y-2 text-sm" data-testid="auto-booking-details">
      <Row icon={MapPin} label={t("hosts.manage_for")} value={booking.nickname_or_address} />
      <Row icon={Calendar} label={t("hosts.manage_on")} value={formatDate(booking.preferred_date, locale)} />
      <Row icon={Clock} label={t("hosts.f_time")} value={t(`wizard.step6.time_${booking.preferred_time_slot}`)} />
      <Row label={t("hosts.manage_total")} value={`$${(booking.total || 0).toFixed(2)}`} />
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[#4B5563] flex items-center gap-1.5">{Icon && <Icon className="w-3.5 h-3.5" />}{label}</span>
      <span className="font-semibold text-right">{value}</span>
    </div>
  );
}

export default function AutoCancelPage() {
  const { token } = useParams();
  const { t, locale } = useI18n();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/hosts/auto/${token}`).then((r) => setBooking(unwrap(r)))
      .catch((e) => setError(e?.response?.status === 404 ? "invalid" : "error"))
      .finally(() => setLoading(false));
  }, [token]);

  const doCancel = async () => {
    setBusy(true);
    try {
      await api.post(`/hosts/auto/${token}/cancel`);
      setCancelled(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not cancel");
    } finally { setBusy(false); }
  };

  return (
    <AutoBookingShell>
      <SEO title="Cancel cleaning — Ever Clean Home" noindex />
      <Card className="rounded-3xl p-8 border border-[#E5E7EB] max-w-md w-full" data-testid="auto-cancel-page">
        {loading ? (
          <div className="grid place-items-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" /></div>
        ) : error ? (
          <div className="text-center py-6" data-testid="auto-cancel-error">
            <XCircle className="w-10 h-10 mx-auto text-[#9CA3AF]" />
            <p className="mt-3 text-[#4B5563]">{t("hosts.manage_invalid")}</p>
          </div>
        ) : cancelled ? (
          <div className="text-center py-4" data-testid="auto-cancel-done">
            <CheckCircle2 className="w-12 h-12 mx-auto text-[#0EA5A4]" />
            <h1 className="font-display text-2xl font-extrabold tracking-tighter mt-4">{t("hosts.cancel_done")}</h1>
            <p className="mt-2 text-[#4B5563]">{t("hosts.cancel_done_body")}</p>
            <Link to="/"><Button variant="outline" className="rounded-xl mt-6">{t("hosts.back_home")}</Button></Link>
          </div>
        ) : (
          <>
            <h1 className="font-display text-2xl font-extrabold tracking-tighter">{t("hosts.cancel_title")}</h1>
            <p className="mt-2 text-sm text-[#4B5563]">{t("hosts.cancel_sub")}</p>
            <div className="mt-5"><BookingDetails booking={booking} t={t} locale={locale} /></div>
            {booking.cancellable === false ? (
              <p className="mt-4 text-sm text-amber-700" data-testid="auto-cancel-expired">{t("hosts.manage_expired")}</p>
            ) : (
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Button onClick={doCancel} disabled={busy} className="rounded-xl bg-red-600 hover:bg-red-700 text-white flex-1" data-testid="auto-cancel-confirm">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("hosts.cancel_confirm")}
                </Button>
                <Link to={`/auto-reschedule/${token}`} className="flex-1"><Button variant="outline" className="rounded-xl w-full" data-testid="auto-cancel-keep">{t("hosts.resched_title")}</Button></Link>
              </div>
            )}
          </>
        )}
      </Card>
    </AutoBookingShell>
  );
}
